import { FC, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { IconArrowRightBold } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';

import { useUBL } from '@common/log-manager';
import { Typography } from '@common/styles';
import { BeautyOneLineReview, useTabReplacer } from '@domains/pdp-common';

import { OneLineReviewItem } from './OneLineReviewItem';

interface Props {
  oneLineReview: BeautyOneLineReview;
}

export const OneLineReview: FC<React.PropsWithChildren<Props>> = ({ oneLineReview }) => {
  const {
    query: { catalog_product_id },
  } = useRouter();
  const { replaceTab } = useTabReplacer();
  const { ref: more_ref, inView: in_view } = useInView({ threshold: 0.5 });
  const sendUBL = useUBL();

  const more_ubl = useMemo(
    () => ({
      navigation: 'product_detail',
      object_section: 'beauty_info_summary',
      object_button: 'button',
      object_id: 'see_more',
      data: {
        original_product_id: catalog_product_id ? String(catalog_product_id) : '',
      },
    }),
    [catalog_product_id],
  );

  const { orderer_attribute_list, most_answered_review_question } = oneLineReview;
  const hasOrdererAttribute = orderer_attribute_list.length > 0;
  const hasMostAnsweredReview = !!most_answered_review_question;

  const goReviewTab = () => {
    replaceTab('REVIEW');
  };

  useEffect(() => {
    if (in_view) {
      sendUBL({
        category: 'impression',
        ...more_ubl,
      });
    }
  }, [sendUBL, more_ubl, in_view]);

  const handleMoreClick = () => {
    sendUBL({
      category: 'click',
      ...more_ubl,
    });
    goReviewTab();
  };

  if (!hasOrdererAttribute && !hasMostAnsweredReview) {
    return null;
  }

  return (
    <Root>
      <Title>
        <p className={Typography.BODY_15_SEMIBOLD}>한줄평 리뷰</p>
        <ShowMore ref={more_ref} className={Typography.BODY_13_SEMIBOLD} onClick={handleMoreClick}>
          더 보기
          <IconArrowRightBold size={16} v3Color='gray400' />
        </ShowMore>
      </Title>
      <List>
        {hasOrdererAttribute && (
          <OneLineReviewItem
            subject='맞춤 정보'
            reviews={orderer_attribute_list.map((item) => ({
              comment: item.content_html,
              percent: item.order_percent,
            }))}
          />
        )}
        {hasMostAnsweredReview && (
          <OneLineReviewItem
            subject={most_answered_review_question.question}
            reviews={[
              {
                comment: `<b>${most_answered_review_question.answered}</b>`,
                percent: most_answered_review_question.percent,
              },
            ]}
          />
        )}
      </List>
    </Root>
  );
};

const Root = styled.div`
  border: 1px solid ${vars.v3Color.gray150};
  border-radius: 8px;
  margin-bottom: 12px;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 10px 12px 16px;
  height: 44px;
`;

const ShowMore = styled.button`
  display: flex;
  align-items: center;
  color: ${vars.color.textTertiary};
`;

const List = styled.ul`
  padding: 6px 16px 10px;
`;
